import * as React from "react"

import { cn } from "../../lib/utils"

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>

/**
 * v5.2 Input — the native `<input>` styled via `.ac-input`.
 * 44px height, radius-lg, subtle inner shadow, mint focus halo.
 * All visuals come from globals.css; this is the React surface.
 *
 * For leading/trailing adornments wrap with <InputGroup />.
 *
 * ## Character counter rule (applies to Input and Textarea)
 *
 * When a field has a max-character limit, render the counter inside
 * the field's `<FormDescription>` using a split-row layout:
 *
 *   <FormDescription className="flex justify-between">
 *     <span>Hint text describing what goes in the field.</span>
 *     <span className="text-xs">{field.value?.length ?? 0} / {MAX}</span>
 *   </FormDescription>
 *
 * Never put the counter in the label row (clutters the heading and
 * competes with the FormLabel for attention). For `GameRichTextEditor`
 * fields, the editor renders its own counter — `FormDescription` is
 * just prose mentioning the range (e.g. "10-2000 characters").
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      className={cn(
        "ac-input",
        // Calendar pickers: read in dark mode
        (type === "date" || type === "datetime-local") &&
          "[&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer",
        className
      )}
      {...props}
    />
  )
)
Input.displayName = "Input"

export { Input }
